// Page entry. PRD 01 §1.2:
//   - PATIENT:NO_PERMISSION → LNB hidden + entry blocked (rendered as the
//     NoPermissionGate below if a user navigates directly to /patient-management).
//   - READ / WRITE / Root → see the page; component-level write/root checks
//     gate memo editing, manual close, registration, and CSV download.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, UserPlus } from 'lucide-react';

import { useAuthStore } from '@/app/store/auth.store';
import { useRequirePermission } from '@/shared/hooks/useRequirePermission';
import { PatientDetailPanel } from '@/features/patients/components/PatientDetailPanel';
import { PatientListFilters } from '@/features/patients/components/PatientListFilters';
import { PatientListView } from '@/features/patients/components/PatientListView';
import { PatientRegistrationModal } from '@/features/patients/components/PatientRegistrationModal';
import {
  useExportPatients,
  useResendInstallSms,
} from '@/features/patients/hooks/use-patient-mutations';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { usePatientListStore } from '@/features/patients/store/patient-list.store';
import { usePatientDetailStore } from '@/features/patients/store/patient-detail.store';
import { PatientApiError } from '@/features/patients/types/patient.types';
import { toast } from '@/components/feedback/useToast';
import { csvFilename } from '@/shared/utils/csv';

export function PatientManagementPage() {
  const { t } = useTranslation('admin');
  const canRead = useRequirePermission('PATIENT', 'READ');
  // PRD §1.2 + §3.7 + §7: New-patient registration AND CSV download are
  // Root-only. Non-Root sees no buttons at all (vs. disabled-with-tooltip) per
  // the PRD's "hidden" rule.
  const isRoot = useAuthStore((s) => s.isRoot);
  const filters = usePatientListStore((s) => s.filters);
  const sort = usePatientListStore((s) => s.sort);
  const openDetail = usePatientDetailStore((s) => s.open);
  const exportMutation = useExportPatients();
  const [registerOpen, setRegisterOpen] = useState(false);

  // [US-PM-07] App-install SMS send failure. PRD §3.7 등록 안내 SMS: 발송 실패 시 오류 내용 + 실패
  // 팝업(모달), 모달에 [다시 발송] / [닫기]. Held at page level so it survives the registration modal
  // closing — the patient IS registered by this point; only the SMS failed.
  const resendSmsMutation = useResendInstallSms();
  const [smsFailure, setSmsFailure] = useState<{
    patientId: number;
    target: string;
    reason: string | null;
  } | null>(null);

  const handleResendSms = async () => {
    if (!smsFailure) return;
    try {
      await resendSmsMutation.mutateAsync(smsFailure.patientId);
      setSmsFailure(null);
      toast.info(
        t('patientManagement.toasts.smsDispatched', { target: smsFailure.target }),
      );
    } catch (e) {
      // Stay open on a repeat failure — closing would strand the operator with no way back to the
      // retry. Only the reason line is replaced, per PRD "오류 내용 + 실패 팝업".
      setSmsFailure((prev) =>
        prev
          ? {
              ...prev,
              reason:
                e instanceof PatientApiError
                  ? e.message
                  : t('patientManagement.smsFailure.message'),
            }
          : prev,
      );
    }
  };

  // FEATURE_062: the backend renders + streams the CSV (Root-only, raw PHI, Korean headers). The FE just
  // downloads the returned Blob — no client-side CSV building. Honors the current filter + sort; the export
  // endpoint bypasses pagination. Filename mirrors the BE: `patients_YYYYMMDD_HHmmss.csv`.
  const handleDownloadCsv = async () => {
    try {
      const blob = await exportMutation.mutateAsync({
        q: filters.search.trim() || undefined,
        status: filters.status === 'all' ? undefined : filters.status,
        sortBy: sort.key,
        sortDir: sort.dir,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = csvFilename('patients');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const message =
        e instanceof PatientApiError ? e.message : t('patientManagement.toasts.downloadError');
      toast.error(message);
    }
  };

  if (!canRead) return null;

  return (
    <>
      <div className="content__header">
        <div>
          <h1 className="content__title">{t('patientManagement.title')}</h1>
        </div>
        {isRoot && (
          <div className="content__actions">
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={handleDownloadCsv}
              disabled={exportMutation.isPending}
            >
              <Download size={16} />
              {t('patientManagement.actions.downloadCsv')}
            </button>
            <button
              type="button"
              className="btn btn-default btn-primary"
              onClick={() => setRegisterOpen(true)}
            >
              <UserPlus size={16} />
              {t('patientManagement.actions.registerPatient')}
            </button>
          </div>
        )}
      </div>
      <div className="content__body">
        <PatientListFilters />
        <PatientListView onRowClick={openDetail} />
      </div>
      <PatientDetailPanel />
      <PatientRegistrationModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onSmsFailure={({ patientId, target }) =>
          setSmsFailure({ patientId, target, reason: null })
        }
      />
      <ConfirmModal
        open={smsFailure !== null}
        title={t('patientManagement.smsFailure.title')}
        message={smsFailure?.reason ?? t('patientManagement.smsFailure.message')}
        confirmLabel={t('patientManagement.smsFailure.resend')}
        cancelLabel={t('patientManagement.smsFailure.close')}
        loading={resendSmsMutation.isPending}
        loadingLabel={t('patientManagement.smsFailure.resending')}
        onConfirm={handleResendSms}
        onCancel={() => setSmsFailure(null)}
      />
    </>
  );
}
